import React, { useEffect, useState } from "react";
import {
  Button,
  DropdownField,
  Modal,
  TextAreaField,
  TextField,
} from "../../../components/ui";
import type {
  BrandService,
  NewBrandService,
  ServiceGoal,
} from "../types/brand.types";
import CustomNameField from "./CustomNameField";

/** Lets the footer's submit button live outside the `<form>` element. */
const FORM_ID = "service-form";

/**
 * How long a description may run. The card under it clamps at two lines, so
 * this is roughly what fits before the ellipsis — a ceiling that matches what
 * will actually be read, rather than one picked off a column width.
 */
const MAX_DESCRIPTION = 160;

interface ServiceFormModalProps {
  open: boolean;
  /**
   * When set, the dialog edits this service instead of creating one.
   *
   * An edit is a different form, not the same one filled in: the `PATCH` takes
   * a name and a description and nothing else, so the goal a curated service
   * was made from is fixed — and a dropdown of goals in an edit would offer a
   * change the endpoint cannot save. The name becomes a plain box, which is
   * what a rename is.
   */
  service?: BrandService | null;
  /**
   * The goals to offer. Suggestions only — the list is never all of them,
   * which is what the "Add custom service" row under it is for, and a service
   * made through that row has no goal behind it.
   */
  suggestions: ServiceGoal[];
  /** True while the save is with the server; the submit button reads it. */
  saving?: boolean;
  onClose: () => void;
  onSubmit: (service: NewBrandService) => void;
}

/**
 * The create and edit dialog: a name — picked from the suggested goals, or
 * typed — and a line saying what the service is.
 *
 * Two fields and no more, because that is the whole record — what a service
 * costs and how long it runs belong to the plans sold against it, and asking
 * for either here is what made the two collapse into one thing before.
 */
const ServiceFormModal: React.FC<ServiceFormModalProps> = ({
  open,
  service,
  suggestions,
  saving = false,
  onClose,
  onSubmit,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  /**
   * The goal the name was picked from, and `null` while it is being typed.
   *
   * Held rather than looked up at submit time: it is what makes this a curated
   * service rather than a custom one with the same words in it, and matching
   * the text back against the list would turn a typed name that happens to
   * read like a goal into one.
   */
  const [goal, setGoal] = useState<ServiceGoal | null>(null);
  // While set, the name is typed rather than picked. Entered through the
  // list's "Add custom service" row, and left by the chevron beside the box.
  const [custom, setCustom] = useState(false);

  const editing = !!service;

  // Every open starts from the service being edited, or from a blank form —
  // never from the last attempt.
  useEffect(() => {
    if (!open) return;
    setName(service?.name ?? "");
    setDescription(service?.description ?? "");
    // Carried through untouched on an edit: it is not something this form can
    // change, but it is part of the draft, and dropping it here would report a
    // curated service as custom to anything reading what was submitted.
    setGoal(service?.goal ?? null);
    setCustom(false);
  }, [open, service]);

  // The description is optional: plenty of services are clear from their name
  // alone, and the card simply draws nothing where the line would go.
  const valid = !!name.trim();

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid || saving) return;
    onSubmit({
      name: name.trim(),
      description: description.trim(),
      // Only ever the goal behind a picked name: `custom` is the state where
      // there is none, and it clears this on the way in.
      goal,
    });
  };

  return (
    <Modal
      open={open}
      title={editing ? "Edit service" : "Create new service"}
      onClose={onClose}
      footer={
        <>
          <Button
            variant="secondary"
            size="md"
            onClick={onClose}
            // A save is already with the server; cancelling would leave the
            // dialog gone and the card about to arrive with nothing to land in.
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            size="md"
            form={FORM_ID}
            disabled={!valid}
            loading={saving}
          >
            {editing ? "Save changes" : "Create service"}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={handleSubmit}>
        {editing ? (
          <TextField
            label="Service name"
            value={name}
            placeholder="Write the service name"
            onValueChange={setName}
            hint={
              // Said rather than left to be discovered: the name on a curated
              // service reads as the goal's, and renaming it does not move the
              // service to another goal.
              service?.goal
                ? `Created from the ${service.goal.name} goal, which a rename does not change.`
                : undefined
            }
          />
        ) : custom ? (
          <CustomNameField
            label="Write the service name"
            value={name}
            placeholder="Write the service name"
            dismissLabel="Pick from the suggested services"
            onValueChange={setName}
            // Back to the list, and empty: the box is left because what was
            // typed into it is not what was wanted.
            onDismiss={() => {
              setCustom(false);
              setName("");
            }}
          />
        ) : (
          <DropdownField
            label="Write the service name"
            value={name}
            placeholder="Select service"
            options={suggestions.map((suggestion) => suggestion.name)}
            onValueChange={(picked) => {
              setName(picked);
              // The row behind the text, which is what the create posts. A
              // name the list does not hold cannot arrive here — the dropdown
              // only ever hands back one of its own options — so a miss falls
              // to a custom service rather than to a wrong id.
              setGoal(
                suggestions.find((suggestion) => suggestion.name === picked) ??
                  null
              );
            }}
            // The list grows with the backend's, and a dietitian who knows
            // what they are looking for should not have to scroll for it.
            searchable
            footerAction={{
              label: "Add custom service",
              onSelect: (typed) => {
                setCustom(true);
                // Typed from here on, so whatever was picked before is not
                // what this service is any more.
                setGoal(null);
                // Carried over rather than cleared: the box the row swaps in
                // sits where this one was, and text that vanished as the
                // control changed under the cursor would read as a bug.
                setName(typed);
              },
            }}
          />
        )}

        <TextAreaField
          className="mt-4"
          label="Description"
          value={description}
          rows={3}
          placeholder="Say what this service is, in a line"
          maxLength={MAX_DESCRIPTION}
          hint={`${description.length}/${MAX_DESCRIPTION}`}
          onValueChange={setDescription}
        />
      </form>
    </Modal>
  );
};

export default ServiceFormModal;
